import type {
  InventoryResource,
  InventorySnapshotVolume,
  Snapshot,
  Account,
  VolumeSettings,
} from '@repo/api-gw-sdk';
import { Provider, ResourceType } from '@repo/api-gw-sdk';

import { ConvertToEbsSnapshotConfiguration as ConvertToEbsSnapshotConfiguration } from './steps/convertToEbsSnapshotConfiguration';
import { SelectTargetAccountStep } from './steps/selectTargetAccount';

import type { Encryption } from '../encryption/encryptionComponent';
import type { RestoreActionType } from '../panels/instance/tabs/eon-snapshots/vmSnapshotRow';
import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface RestoreVolumeState {
  providerVolumeId: string;
  actionType: RestoreActionType;
  resourceId: string;
  snapshot: Snapshot;
  supportedCloudProviders: Provider[];
  restoreAccount?: Account;
  regionName?: string;
  availabilityZone?: string;
  tags: Record<string, string>;
  initialTags: Record<string, string>;
  keepOriginalTags: boolean;
  volumeSettings?: VolumeSettings;
  initialVolumeSettings?: VolumeSettings;
  keepOriginalSettings?: boolean;
  encryption?: Encryption;
}

const restoreVolumeFlow: Step<RestoreVolumeState> = {
  id: 'select_target_account_step',
  Component: SelectTargetAccountStep,
  next: [
    {
      id: 'convert_to_ebs_snapshot_configuration',
      Component: ConvertToEbsSnapshotConfiguration,
    },
  ],
};

const getVolumeProperties = (
  resource: InventoryResource,
  snapshot: Snapshot,
  providerVolumeId?: string
): InventorySnapshotVolume | undefined => {
  if (resource.resourceType === ResourceType.AwsEbsVolume) {
    return snapshot.resource?.properties?.awsEbsVolume?.volume;
  }

  return snapshot.resource?.properties?.awsEc2?.volumes?.find(
    (x) => x.providerVolumeId === providerVolumeId
  );
};

export const RestoreVolumeWizard = ({
  resource,
  snapshot,
  providerVolumeId,
  actionType,
  onClose,
  onBack,
}: {
  resource: InventoryResource;
  snapshot: Snapshot;
  providerVolumeId: string;
  actionType: RestoreActionType;
  onClose: () => void;
  onBack: () => void;
}) => {
  const volumeProperties = getVolumeProperties(
    resource,
    snapshot,
    providerVolumeId
  );

  if (!volumeProperties || !providerVolumeId) {
    return null;
  }

  const getTitle = () => {
    switch (actionType) {
      case 'convert-snapshot':
        return 'Convert to EBS Snapshot';
      default:
        return 'Restore Volume';
    }
  };

  return (
    <Wizard<RestoreVolumeState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreVolumeFlow]}
      initialState={{
        providerVolumeId,
        actionType,
        resourceId: snapshot.resourceId,
        availabilityZone: volumeProperties.availabilityZone,
        initialVolumeSettings: volumeProperties.volumeSettings,
        initialTags: volumeProperties.tags,
        tags: volumeProperties.tags,
        regionName: volumeProperties.region,
        snapshot,
        supportedCloudProviders: [Provider.Aws],
        keepOriginalTags: true,
        keepOriginalSettings: true,
      }}
      title={getTitle()}
      stepperLabels={['Restore Account', 'Configuration']}
    />
  );
};
